import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {getEq} from 'fp-ts/lib/Array';
import {Eq, eqBoolean, eqNumber, getStructEq} from 'fp-ts/lib/Eq';
import {nanoid} from 'nanoid';
import {
  EncryptedTaxIdentificationNumber,
  eqTaxIdNumber,
  ExistingTaxIdentificationNumber,
  TaxIdentificationNumber,
  UnvalidatedTaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Agent/Policies/models/TaxIdentificationNumber';
import {eqArrayOfAgentNumbers, ValidAgentNumber} from 'Users/models/AgentNumber';

export enum AgencySupportVisibilityPermission {
  Account = 1,
  Policies = 2,
  CommissionsAnd1099s = 3,
  Claims = 4,
  PolicyServiceRequest = 6,
  Correspondence = 8,
  UnderwritingRequirements = 9,
}

export class UnvalidatedAgencyAssociation {
  public readonly key = 'UnvalidatedAgencyAssociation';

  constructor(
    public readonly id: string,
    public readonly isAdministrator: boolean,
    public readonly taxIdNumber: TaxIdentificationNumber,
    public readonly agentNumbers: ValidAgentNumber[],
    public readonly visibilityPermissions: AgencySupportVisibilityPermission[]
  ) {}
}

export class InvalidAgencyAssociation {
  public readonly key = 'InvalidAgencyAssociation';

  constructor(
    public readonly id: string,
    public readonly isAdministrator: boolean,
    public readonly taxIdNumber: TaxIdentificationNumber,
    public readonly agentNumbers: ValidAgentNumber[],
    public readonly visibilityPermissions: AgencySupportVisibilityPermission[]
  ) {}
}

export class ValidAgencyAssociation {
  public static create(
    id: string,
    isAdministrator: boolean,
    taxIdNumber: TaxIdentificationNumber,
    agentNumbers: ValidAgentNumber[],
    visibilityPermissions: AgencySupportVisibilityPermission[]
  ): AgencyAssociation {
    return (taxIdNumber instanceof ValidTaxIdentificationNumber ||
      taxIdNumber instanceof ExistingTaxIdentificationNumber ||
      taxIdNumber instanceof EncryptedTaxIdentificationNumber) &&
      agentNumbers.length > 0
      ? new ValidAgencyAssociation(id, isAdministrator, taxIdNumber, agentNumbers, visibilityPermissions)
      : new InvalidAgencyAssociation(id, isAdministrator, taxIdNumber, agentNumbers, visibilityPermissions);
  }

  public readonly key = 'ValidAgencyAssociation';
  private constructor(
    public readonly id: string,
    public readonly isAdministrator: boolean,
    public readonly taxIdNumber: TaxIdentificationNumber,
    public readonly agentNumbers: ValidAgentNumber[],
    public readonly visibilityPermissions: AgencySupportVisibilityPermission[]
  ) {}
}

export type AgencyAssociation = ValidAgencyAssociation | InvalidAgencyAssociation | UnvalidatedAgencyAssociation;

export type AgencyAssociationOptions<TReturn> = ToMatchOptions<AgencyAssociation, 'key', TReturn>;

export const matchAgencyAssociation = <TReturn>(
  association: AgencyAssociation,
  options: AgencyAssociationOptions<TReturn>
): TReturn => {
  switch (association.key) {
    case 'InvalidAgencyAssociation':
      return options.InvalidAgencyAssociation(association);
    case 'UnvalidatedAgencyAssociation':
      return options.UnvalidatedAgencyAssociation(association);
    case 'ValidAgencyAssociation':
      return options.ValidAgencyAssociation(association);
  }
};

export const eqAgencyAssociation: Eq<AgencyAssociation> = getStructEq({
  agentNumbers: eqArrayOfAgentNumbers,
  taxIdNumber: eqTaxIdNumber,
  isAdministrator: eqBoolean,
  visibilityPermissions: getEq(eqNumber),
});
export const eqArrayOfAgencyAssociations = getEq(eqAgencyAssociation);

export const getEmptyAgencyAssociation = () =>
  new UnvalidatedAgencyAssociation(nanoid(5), false, new UnvalidatedTaxIdentificationNumber(''), [], []);
